import React, { useState } from "react";

import { Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import useGetAssetClasses from "@fartherfinance/frontend/api/Investment/hooks/useGetAssetClasses";

import getAssetClassCategoryLabelFromEnum from "../../helpers/getAssetClassCategoryLabelFromEnum";
import {
  generateCustomAssetClassId,
  NO_ASSET_CLASS_NAME,
} from "../../reducer/reducer";
import {
  AssetCategoryNotCashAndClass,
  AssetClassCategoryNotCash,
} from "../../reducer/Types";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import ModalWithClearButton from "@src/sharedComponents/Modal/ModalWithClearButton";
import RadioButtons from "@src/sharedComponents/Radio/RadioButtons";
import Text from "@src/sharedComponents/Typography/Text";
import { State } from "@src/store";

import styles from "./CreateEditAssetClassModal.module.css";

const RadioButtonsValues = ["Equity", "Fixed Income", "Other"] as const;
type RadioButtonValue = typeof RadioButtonsValues[number];

interface Form {
  TextInput: string;
}

type Props = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (assetCategoryWithClass: AssetCategoryNotCashAndClass) => void;
  assetClassCategory?: AssetClassCategoryNotCash;
  assetClass?: string;
};

const CreateEditAssetClassModal: React.FC<Props> = ({
  title,
  isOpen,
  onClose,
  onSubmit,
  assetClassCategory,
  assetClass,
}): JSX.Element => {
  const [radioButtonValue, setRadioButtonValue] = useState<RadioButtonValue>(
    assetClassCategory ?? "Equity"
  );

  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));

  const auth = useAdvisorRequestAuth();
  const assetClassesAndTheirCategories = useGetAssetClasses(advisorId, auth);

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      TextInput: assetClass ?? "",
    },
  });

  const notify = useStatusNotification();

  const {
    getValues,
    formState: { isValid },
  } = methods;

  const onSave = async () => {
    if (assetClassesAndTheirCategories.data === undefined) {
      return;
    }

    if (!isValid) {
      return;
    }

    const values = getValues();

    if (values.TextInput === NO_ASSET_CLASS_NAME) {
      notify("Cannot use this name for an asset class", "Error");
      return;
    }

    const alreadyExists = assetClassesAndTheirCategories.data.data.some(
      (ac) =>
        ac.assetClassName === values.TextInput.trim() &&
        getAssetClassCategoryLabelFromEnum(ac.assetClassCategory) ===
          radioButtonValue
    );

    if (alreadyExists) {
      notify("This asset class already exists in the dropdown", "Error");
      return;
    }

    onSubmit({
      assetClassId: generateCustomAssetClassId(), // we need to generate a temporary id for the dropdown (to uniquely id a dropdown item) - this id will NOT be sent to the BE
      assetClassName: values.TextInput.trim(),
      category: radioButtonValue,
    });
  };

  return (
    <ModalWithClearButton closeModal={onClose} isOpen={isOpen}>
      <Text className={styles.title} variant="title2">
        {title}
      </Text>

      <Text className={styles.text} variant="body" color="textSubtle">
        Use an asset class to group securities together.
      </Text>

      <FormProvider {...methods}>
        <FormTextField
          name="TextInput"
          label={"Name"}
          placeholder={""}
          rules={{
            validate: {
              hasMinLength: (input) =>
                input.length < 3 ? "Must be at least 3 characters" : undefined,
            },
          }}
        />

        <Text className={styles.label} variant="bodySmall" color={"textSubtle"}>
          Name must be a unique
        </Text>
      </FormProvider>

      <RadioButtons
        label={"Category"}
        values={RadioButtonsValues}
        value={radioButtonValue}
        onChange={setRadioButtonValue}
        isValid={undefined}
      />

      <Stack
        spacing={2}
        direction={"row"}
        marginTop={"40px"}
        justifyContent={"flex-end"}
      >
        <Button
          text="Cancel"
          buttonType="primary"
          variant="outlined"
          disabled={false}
          onClick={onClose}
        />

        <Button
          text="Save"
          buttonType="primary"
          variant="contained"
          disabled={
            !isValid || assetClassesAndTheirCategories.data === undefined
          }
          onClick={onSave}
        />
      </Stack>
    </ModalWithClearButton>
  );
};

export default CreateEditAssetClassModal;
